import { useMemo, useState } from 'react';
import { Add, Check, Close, Delete, Edit } from '@mui/icons-material';
import { Box, Button, Chip, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Table, usePagination } from '@frontend/features/common/components/Table';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import {
  useGetWorkflowSteps,
  useGetWorkflowsByAgency,
} from '@frontend/features/workflows/queries';

import {
  CopilotConfigDto,
  useCopilotConfigs,
  useDeleteCopilotConfig,
} from '../queries';
import { CopilotConfigModal } from './CopilotConfigModal';

/**
 * Renderiza los nombres de los steps de una config. Usa useGetWorkflowSteps por
 * su propio workflowId, así no hay que pre-cargar todos los steps en el padre.
 */
const StepNamesCell = ({
  workflowId,
  stepIds,
}: {
  workflowId: string | null;
  stepIds: string[];
}) => {
  const { t } = useTranslation();
  const { data: steps } = useGetWorkflowSteps(workflowId || undefined);

  if (!stepIds || stepIds.length === 0) {
    return <>{t('copilot.configs.allSteps')}</>;
  }

  const nameById = new Map((steps || []).map((s) => [s.id, s.name]));
  const names = stepIds.map((id) => nameById.get(id) || id);
  const shown = names.slice(0, 2);
  const rest = names.length - shown.length;

  return (
    <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
      {shown.map((name, index) => (
        <Chip key={`${name}-${index}`} label={name} size="small" />
      ))}
      {rest > 0 && (
        <Chip
          label={t('copilot.configs.moreSteps', { count: rest })}
          size="small"
          variant="outlined"
        />
      )}
    </Box>
  );
};

interface Props {
  agencyId: number;
}

const capabilityKeys: {
  key: keyof CopilotConfigDto;
  labelKey: string;
}[] = [
  { key: 'detectClientName', labelKey: 'copilot.configs.form.detectClientName' },
  {
    key: 'detectCustomFields',
    labelKey: 'copilot.configs.form.detectCustomFields',
  },
  {
    key: 'analyzeInterestScore',
    labelKey: 'copilot.configs.form.analyzeInterestScore',
  },
  { key: 'suggestReplies', labelKey: 'copilot.configs.form.suggestReplies' },
  { key: 'salesCoaching', labelKey: 'copilot.configs.form.salesCoaching' },
  { key: 'useExternalApis', labelKey: 'copilot.configs.form.useExternalApis' },
];

export const CopilotConfigsList = ({ agencyId }: Props) => {
  const { t } = useTranslation();
  const pagination = usePagination();
  const { addToast } = useSnackBar();

  const { data, isLoading, refetch } = useCopilotConfigs(agencyId);
  const deleteMutation = useDeleteCopilotConfig(agencyId);

  const { data: workflowsData } = useGetWorkflowsByAgency({
    agencyId: String(agencyId),
    pageSize: 1000,
    currentPage: 1,
    search: '',
  });

  const workflowNameById = useMemo(
    () =>
      (workflowsData?.items || []).reduce<Record<string, string>>((acc, wf) => {
        acc[wf.id] = wf.name;
        return acc;
      }, {}),
    [workflowsData],
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState<CopilotConfigDto | undefined>(
    undefined,
  );

  const handleOpen = (config?: CopilotConfigDto) => () => {
    setSelected(config);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setSelected(undefined);
    setIsModalOpen(false);
    refetch();
  };

  const handleDelete = (config: CopilotConfigDto) => async () => {
    const confirmed = window.confirm(t('copilot.configs.confirmDelete'));
    if (!confirmed) return;
    try {
      await deleteMutation.mutateAsync(config.id);
      addToast({ message: t('copilot.configs.deleteSuccess'), color: 'success' });
      refetch();
    } catch {
      addToast({ message: t('copilot.configs.deleteError'), color: 'error' });
    }
  };

  const renderCapabilities = (config: CopilotConfigDto) => {
    const active = capabilityKeys.filter((c) => config[c.key]);
    if (active.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      );
    }
    return (
      <Box display="flex" gap={0.5} flexWrap="wrap">
        {active.map((c) => (
          <Chip key={c.key} label={t(c.labelKey)} size="small" />
        ))}
      </Box>
    );
  };

  const count = data?.length || 0;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" alignItems="center" gap={2}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {t('copilot.configs.title')}
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpen()}>
          {t('copilot.configs.createCta')}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {t('copilot.configs.subtitle')}
      </Typography>

      {isModalOpen && (
        <CopilotConfigModal
          open={isModalOpen}
          onClose={handleClose}
          agencyId={agencyId}
          config={selected}
        />
      )}

      {count > 0 && (
        <Table
          height={360}
          columns={[
            { id: 'actions', label: t('copilot.configs.table.actions'), align: 'left' },
            { id: 'workflow', label: t('copilot.configs.table.workflow'), align: 'left' },
            { id: 'steps', label: t('copilot.configs.table.steps'), align: 'left' },
            { id: 'capabilities', label: t('copilot.configs.table.capabilities'), align: 'left' },
            { id: 'enabled', label: t('copilot.configs.table.enabled'), align: 'left' },
          ]}
          data={{
            key: 'id',
            items: (data || []).map((item) => ({
              ...item,
              workflow: item.workflowId
                ? workflowNameById[item.workflowId] || item.workflowId
                : t('copilot.configs.allWorkflows'),
              steps: (
                <StepNamesCell
                  workflowId={item.workflowId}
                  stepIds={item.stepIds}
                />
              ),
              capabilities: renderCapabilities(item),
              enabled: item.enabled ? (
                <Check color="success" />
              ) : (
                <Close color="error" />
              ),
              actions: (
                <>
                  <IconButton onClick={handleOpen(item)}>
                    <Edit color="primary" />
                  </IconButton>
                  <IconButton onClick={handleDelete(item)}>
                    <Delete color="error" />
                  </IconButton>
                </>
              ),
            })),
            totalCount: count,
          }}
          pagination={pagination}
          stickyHeader
        />
      )}

      {count === 0 && !isLoading && (
        <Typography color="text.secondary">
          {t('copilot.configs.empty')}
        </Typography>
      )}
    </Box>
  );
};
